import { ASSIGNED_SEATING, enums, optionsByPK } from "@shared/constants";
import useSeatingWaitlistHelpers from "@shared/hooks/seating/useSeatingWaitlistHelpers/useSeatingWaitlistHelpers";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import {
  MapPermissionsType,
  SeatingSelectReturnType,
  SeatWithLocationDataType,
} from "@shared/modules/seating/types/seating-types";
import { useCallback, useMemo } from "react";
import {
  MapItemType,
  WaitlistSelectModalProps,
} from "./WaitlistSelectModal.props";

export function useWaitlistSelectModal(
  props: WaitlistSelectModalProps & {
    multiItemSelections: Array<SeatingSelectReturnType>;
    updateMultiItemSelections: (input: Array<SeatingSelectReturnType>) => void;
  }
) {
  const {
    itemsOnSeat,
    ticketAuthArray,
    multiItemSelections,
    updateMultiItemSelections,
    onClose,
  } = props;
  const { $data, $permissions, $name } = itemsOnSeat;

  const {
    seatingServiceState,
    getInfoRequiredLocationQtyInCart,
    getLocationQtyInCart,
  } = useSeatingState();

  const { getInfoRequiredWaitlistSeatQty, getWaitlistSeatQty } =
    useSeatingWaitlistHelpers();

  // Public basket is the default type
  const { itemGroups } = useBasketService();

  const isSeat = useMemo(() => $name === ASSIGNED_SEATING.SEAT.ID, [$name]);
  const isLocation = useMemo(
    () => $name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID,
    [$name]
  );

  const getSelectedLocationQty = useCallback(
    (itemId: number, permissionId: number) => {
      if (seatingServiceState.getCartPermission(permissionId)) {
        // info_required items with locations are split into different item_groups
        // the quantity will be the number of item_groups with the same id value
        const itemGroup = itemGroups?.find(
          (ig) => ig.type === itemId || ig.membership_level === itemId
        );

        if (itemGroup?.info_required) {
          return getInfoRequiredLocationQtyInCart(itemId, permissionId, 0);
        }

        return getLocationQtyInCart(itemId, permissionId, 0);
      }

      return 0;
    },
    [
      itemGroups,
      seatingServiceState,
      getInfoRequiredLocationQtyInCart,
      getLocationQtyInCart,
    ]
  );

  const getSelectedSeatQty = useCallback(
    (itemId: number) => {
      const itemsData = $data as SeatWithLocationDataType;

      // info_required items with seats are split into different item_groups
      // the quantity will be the number of item_groups with the same id value
      const itemGroup = itemGroups?.find(
        (ig) => ig.type === itemId || ig.membership_level === itemId
      );

      if (itemGroup?.info_required) {
        return getInfoRequiredWaitlistSeatQty(
          itemId,
          Number(itemsData.locationData?.segment_id),
          0
        );
      }

      return getWaitlistSeatQty(
        itemId,
        Number(itemsData.locationData?.segment_id),
        0
      );
    },
    [itemGroups, $data, getInfoRequiredWaitlistSeatQty, getWaitlistSeatQty]
  );

  // if the cart permission exists, return the basket quantity
  // else return 0
  const getCurrentQtyInBasket = useCallback(
    (itemId: number, permissionId: number): number => {
      if (isLocation) {
        return getSelectedLocationQty(itemId, permissionId);
      }
      if (isSeat) {
        return getSelectedSeatQty(itemId);
      }

      return 0;
    },
    [isSeat, isLocation, getSelectedLocationQty, getSelectedSeatQty]
  );

  // get the current selected amounts for each item type
  // either from the basket or from the multiItemSelections array
  const getCurrentSelectedAmount = useCallback(
    (
      item: SeatingItemPropertyMap & {
        permissionId: number;
      }
    ): number => {
      if (
        !multiItemSelections.length ||
        !multiItemSelections.find((loc) => loc.itemTypeId === item.id)
      ) {
        return getCurrentQtyInBasket(item.id, item.permissionId) ?? 0;
      }
      return (
        multiItemSelections.find((loc) => loc.itemTypeId === item.id)
          ?.requestAmount ?? 0
      );
    },
    [multiItemSelections, getCurrentQtyInBasket]
  );

  const selectItemPermission = useCallback(
    ({
      itemTypeId,
      permissionId,
      minPurchaseLimit,
      requestAmount,
    }: {
      itemTypeId: number;
      permissionId: number;
      minPurchaseLimit?: number;
      requestAmount?: number;
    }) => {
      if (itemsOnSeat) {
        const returnData = {
          itemTypeId,
          permissionId,
          requestAmount,
          ticketAuthArray,
          minPurchaseLimit,
        };

        const newMultiItemSelections = multiItemSelections
          .filter((selection) => selection.itemTypeId !== returnData.itemTypeId)
          .concat([returnData]);
        updateMultiItemSelections(newMultiItemSelections);
      }
    },
    [ticketAuthArray, itemsOnSeat, multiItemSelections]
  );

  const locationItems = useMemo(() => {
    return $permissions.map((permission: MapPermissionsType) => {
      const cartLocationPermission = seatingServiceState.getCartPermission(
        Number(permission.seatingItemPermissionId)
      );
      const itemType: SeatingItemPropertyMap =
        seatingServiceState?.itemTypeMap[
          permission.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
        ];
      const requestAmount =
        (cartLocationPermission && cartLocationPermission?.location?.count) ||
        0;

      // If this item type is waitlisted, ignore the usages remaining
      const usageRemaining = Number(itemType?.$countOptions?.length) - 1;

      const $countOptions =
        itemType?.$countOptions?.filter(
          (option: number) => option <= usageRemaining
        ) ?? [];

      return {
        ...itemType,
        $countOptions,
        permissionId: Number(permission.seatingItemPermissionId),
        requestAmount,
        usageRemaining,
      };
    });
  }, [$permissions, seatingServiceState]);

  const seatItems = useMemo(() => {
    // default permissions array is $permissions
    let permissionsArray = $permissions;

    // if there are seats in the pending cart,
    // get the item type in the pending cart and only show that item type on the selection modal
    if (seatingServiceState.pendingCartPermissions.length > 0) {
      permissionsArray = $permissions.filter(
        (p) =>
          p.seatingItemTypeId ===
          seatingServiceState.pendingCartPermissions[0].itemTypeId
      );
    }
    return permissionsArray.map((p) => {
      const itemType: SeatingItemPropertyMap =
        seatingServiceState.itemTypeMap[
          p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
        ];
      return {
        ...itemType,
        permissionId: Number(p.seatingItemPermissionId),
      };
    });
  }, [$permissions, seatingServiceState]);

  // get the correct item type for modal where multiple items can be selected at once
  // example: waitlist modals and GA location modals
  const itemTypes: Array<MapItemType> = useMemo(() => {
    if (isLocation) {
      return locationItems;
    }
    if (isSeat) {
      return seatItems;
    }
    return [];
  }, [isLocation, isSeat, locationItems, seatItems]);

  const isTableSeat = useMemo(() => {
    const allTableTypes = Object.keys(
      optionsByPK(enums.SEATING_LOCATION_TYPE_MAP)
    ).filter((obj) => obj.includes("table"));

    return (
      isSeat &&
      allTableTypes.includes(
        ($data as SeatWithLocationDataType).locationData?.type ?? ""
      )
    );
  }, [isSeat, $data]);

  const cancelModal = () => {
    onClose(null);
  };

  return {
    cancelModal,
    selectItemPermission,
    getCurrentSelectedAmount,
    isTableSeat,
    itemTypes,
    isSeat,
  };
}
